import { runInAction } from "mobx";
import { v4 } from "uuid";

import { usePlayground } from "@/components/Playground/playground-store";

/**
 * Hook for managing input variables in the playground.
 * Provides functions to ensure, update, get, remove, and rename variables
 * across all variable sets.
 *
 * Note: Functions are not memoized since they operate on a stable MobX store.
 * Consumers should not include these in useEffect dependency arrays.
 */
export const useInputVariables = () => {
  const playground = usePlayground();

  /**
   * Ensures a variable exists in all variable sets.
   * If the variable doesn't exist, adds it with an empty value.
   */
  const ensureVariable = (variableName: string) => {
    if (!variableName) {
      return;
    }

    runInAction(() => {
      playground.input_variable_sets.forEach(set => {
        const exists = set.variables.some(v => v.key === variableName);
        if (!exists) {
          set.variables.push({
            id: v4(),
            key: variableName,
            value: "",
          });
        }
      });
    });
  };

  /**
   * Updates the value of a variable across all variable sets.
   */
  const updateVariableValue = (variableName: string, value: string) => {
    if (!variableName) {
      return;
    }

    runInAction(() => {
      playground.input_variable_sets.forEach(set => {
        const variable = set.variables.find(v => v.key === variableName);
        if (variable) {
          variable.value = value;
        }
      });
    });
  };

  /**
   * Gets the value of a variable from the first variable set.
   */
  const getVariableValue = (variableName: string): string | null => {
    if (!variableName) {
      return null;
    }
    const firstSet = playground.input_variable_sets[0];
    if (!firstSet) {
      return null;
    }

    const variable = firstSet.variables.find(v => v.key === variableName);
    return (variable?.value as string) ?? null;
  };

  /**
   * Removes a variable from all variable sets.
   */
  const removeVariable = (variableName: string) => {
    if (!variableName) {
      return;
    }

    runInAction(() => {
      playground.input_variable_sets.forEach(set => {
        const index = set.variables.findIndex(v => v.key === variableName);
        if (index !== -1) {
          set.variables.splice(index, 1);
        }
      });
    });
  };

  /**
   * Renames a variable key across all variable sets.
   * Preserves the value.
   */
  const renameVariable = (oldName: string, newName: string) => {
    if (!oldName || !newName) {
      return;
    }

    runInAction(() => {
      playground.input_variable_sets.forEach(set => {
        const variable = set.variables.find(v => v.key === oldName);
        if (variable) {
          variable.key = newName;
        }
      });
    });
  };

  /**
   * Gets the value of a variable from a specific variable set.
   */
  const getVariableValueForSet = (setId: string, variableName: string): string | null => {
    if (!variableName || !setId) {
      return null;
    }
    const set = playground.input_variable_sets.find(s => s.id === setId);
    if (!set) {
      return null;
    }

    const variable = set.variables.find(v => v.key === variableName);
    return (variable?.value as string) ?? null;
  };

  /**
   * Updates the value of a variable in a specific variable set.
   */
  const updateVariableValueForSet = (setId: string, variableName: string, value: string) => {
    if (!variableName || !setId) {
      return;
    }

    runInAction(() => {
      const set = playground.input_variable_sets.find(s => s.id === setId);
      if (set) {
        const variable = set.variables.find(v => v.key === variableName);
        if (variable) {
          variable.value = value;
        }
      }
    });
  };

  return {
    inputVariables: playground.input_variables,
    inputVariableSets: playground.input_variable_sets,
    ensureVariable,
    updateVariableValue,
    getVariableValue,
    removeVariable,
    renameVariable,
    getVariableValueForSet,
    updateVariableValueForSet,
  };
};
